/// <amd-module name="Widgets/AssignedToMeWidget/AssignedToMeWidget" />
import { QueryType, WorkItemFieldReference, WorkItemQueryResult } from "Dashboards/Clients/Contracts/WorkItemTracking";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import * as React from "react";
import { NoDataMessage } from "Reporting/Visuals/VisualMessages/NoDataMessage";
import { IProjectData } from "Tfs/Platform/Page";
import * as TfsWidgetContext from "Tfs/Widget/Context";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getSuggestedOpenWorkItemBehavior, WorkItemResultsGrid } from "WidgetCommon/WorkItemResultsGrid/WorkItemResultsGrid";
import { WorkItemsAssignedToMeQuery, WorkItemTypeDetailsQuery, WorkItemTypesQuery } from "Widgets/AssignedToMeWidget/AssignedToMeQueries";
import * as Resources from "Widgets/AssignedToMeWidget/Resources";

export interface AssignedToMeWidgetState {
    workItemIds: number[];
    workItemsCount: number;
    columns: WorkItemFieldReference[];
    queryType: QueryType;
}

export class AssignedToMeWidget extends VssComponent<WidgetProps, AssignedToMeWidgetState> {
    private project: IProjectData;
    private userName: string;
    private _isMounted: boolean;

    constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props);
        this.project = TfsWidgetContext.getProject(context.pageContext);

        // Retrieve user name for displaying in widget title.
        this.userName = TfsWidgetContext.getUserName(context.pageContext);
    }

    public async componentDidMount() {
        this._isMounted = true;
        const cacheableQueryService = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");

        try {
            // Retrieve work item type data and construct wiql for query.
            const workItemTypesQuery = new WorkItemTypesQuery(this.project.id);
            const workItemTypeNames = await cacheableQueryService.getCacheableQueryResult(workItemTypesQuery);
            const workItemTypeDetailsQuery = new WorkItemTypeDetailsQuery(this.project.id, workItemTypeNames);
            const workItemTypes = await cacheableQueryService.getCacheableQueryResult(workItemTypeDetailsQuery);

            // Retrieve work items using generated wiql.
            const workItemsAssignedToMeQuery = new WorkItemsAssignedToMeQuery(workItemTypes, this.project);
            const workItemsAssignedToMe: WorkItemQueryResult = await cacheableQueryService.getCacheableQueryResult(workItemsAssignedToMeQuery);
            const workItemIds = workItemsAssignedToMe.workItems.map<number>(workItem => workItem.id);
            const workItemsCount = workItemIds ? workItemIds.length : 0;

            if (this._isMounted) {
                this.setState(
                    {
                        workItemIds: workItemIds,
                        workItemsCount: workItemsCount,
                        columns: workItemsAssignedToMe.columns,
                        queryType: workItemsAssignedToMe.queryType
                    },
                    this.props.onWidgetLoaded
                );
                WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
            }
        } catch (error) {
            this.props.onWidgetLoaded({
                outcome: LoadingStatus.failure,
                outcomeDescription: error,
                extendedDiagnosticDetail: error && error.extendedDiagnosticInfo
            });
        }
    }

    public render(): JSX.Element | null {
        if (!this.state) {
            return null;
        }

        const { workItemIds, columns, queryType } = this.state;

        if (workItemIds && workItemIds.length === 0) {
            return (
                <div className={"assigned-to-me-widget"}>
                    <NoDataMessage visualTitle={this.getTitle()} size={this.props.size} cardId={3} />
                </div>
            );
        }

        return (
            <div className={"assigned-to-me-widget"}>
                <WorkItemResultsGrid
                    title={this.getTitle()}
                    columns={this.getSizeAppropriateColumns(columns)}
                    itemsSource={workItemIds}
                    projectId={this.project.id}
                    widgetPropsForTelemetry={this.props}
                    size={this.props.size}
                    noDataCardId={2}
                    showVisualFilters
                    queryType={queryType}
                    openWorkItemBehavior={getSuggestedOpenWorkItemBehavior(this.props.avoidShowingModalUI)}
                />
            </div>
        );
    }

    public getSizeAppropriateColumns(columns: WorkItemFieldReference[]): WorkItemFieldReference[] {
        if (this.props.size.width <= 330) {
            const reducedColumns: WorkItemFieldReference[] = [];
            columns.forEach(column => {
                if (column.referenceName !== "System.Id") {
                    reducedColumns.push(column);
                }
            });
            return reducedColumns;
        } else {
            return columns;
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    private getTitle(): string {
        const { workItemsCount } = this.state;
        const workItemsCountString = workItemsCount ? `(${workItemsCount.toString()})` : "";

        return `${format(Resources.AssignedToMe_Title, this.userName)} ${workItemsCountString}`;
    }
}

VssComponent.register("assignedToMeWidget", AssignedToMeWidget);
